// The candidate profile's pure readings (lane B3 S12; UAT D-8): his words for a profile's application — its DC-31
// column, its status, its stage track — a timeline line, a résumé version, the hero's current role and the
// talent-community reason, the profile's route, and which seat may act. Kept apart from the screens so the node suites
// can hold them without rendering.

import type {
  CandidateDuplicateOpenOutput, CandidateProfileOutput, CandidateSearchInput, DuplicateSide, ProfileApplication, ProfileChanges, StageClock, TimelineRow,
} from "@/api/tools";
import {
  APPLICATION_STATUS, DOCUMENT_LABELS, G6_COLUMN_LABELS, G6_LABELS, G7_TEMPLATES, HM_REVIEW_STATUS, PROFILE_STATUS, REQUISITION_STATUS,
  SCREEN_STATUS, type CompareAttribute, type HistoryEvent, type PipelineStage, type RailStep,
} from "@/mc";
import { columnOf } from "@/views/pipeline/pipeline";
import { BRD } from "./brd";
import { CANDIDATE_LABELS as L, PROFILE_LABELS as P, PROFILE_TEMPLATES } from "./labels";

export const PROFILE_TABS = ["overview", "applications", "resumes", "timeline", "talent-community", "internal-mobility"] as const;
export type ProfileTab = (typeof PROFILE_TABS)[number];

/** `#candidates/<token>[/<tab>]` or, for a rediscovered member named by the caller's search ref, `#candidates/ref/<ref>`. */
export function profileHref(subject: { token?: string | null | undefined; ref?: string | null | undefined }, tab?: ProfileTab): string {
  const base = subject.token ? `#candidates/${subject.token}` : `#candidates/ref/${subject.ref ?? ""}`;
  return tab && tab !== "overview" ? `${base}/${tab}` : base;
}

export function tabOf(part: string | undefined): ProfileTab | null {
  if (part === undefined) return "overview";
  return (PROFILE_TABS as readonly string[]).includes(part) ? (part as ProfileTab) : null;
}

export const STATUS_WORD: Readonly<Record<"Active" | "Archived" | "Merged", string>> = {
  Active: PROFILE_STATUS.active,
  Archived: PROFILE_STATUS.archived,
  Merged: PROFILE_STATUS.merged,
};

/** The stages in which an application is no longer live (V1 `enums.LIVE_STAGES`' complement). */
const CLOSED_STAGES = new Set(["not_selected", "passed_on", "withdrawn", "dispositioned", "declined", "reneged", "hired"]);

/** His application status for a stage: "application Sourced -> Qualified or Dispositioned" (BRD 3.5). */
export function applicationStatus(stage: string | null | undefined): string | null {
  if (!stage) return null;
  if (stage === "sourced") return APPLICATION_STATUS.sourced;
  return CLOSED_STAGES.has(stage) ? APPLICATION_STATUS.dispositioned : APPLICATION_STATUS.qualified;
}

/** A recorded disposition's word: the dispositions dictionary's label the server resolved (`code_label`), else the code
 * itself when no version of the dictionary names it (demo smoke D8). */
export function dispositionWord(d: Readonly<Record<string, unknown>> | null | undefined): string {
  const label = d?.["code_label"];
  if (typeof label === "string" && label) return label;
  const code = d?.["code"];
  return typeof code === "string" ? code : "";
}

const SCREEN_WORD: Readonly<Record<string, string>> = {
  requested: SCREEN_STATUS.screenRequested,
  scheduled: SCREEN_STATUS.scheduled,
  completed: SCREEN_STATUS.completed,
  advance: SCREEN_STATUS.advance,
  hold: SCREEN_STATUS.hold,
  pass: SCREEN_STATUS.pass,
  withdrawn: SCREEN_STATUS.withdrawn,
};

/** Step 8 on the application row: the outcome once recorded, else the screen's status. */
export function screenWord(app: Pick<ProfileApplication, "screen_status" | "screen_outcome">): string | null {
  const key = app.screen_outcome ?? (app.screen_status === "outcome" ? null : app.screen_status);
  return key ? SCREEN_WORD[key] ?? null : null;
}

const REQUISITION_WORD: Readonly<Record<string, string>> = {
  draft: REQUISITION_STATUS.created,
  open: REQUISITION_STATUS.posted,
  partially_filled: REQUISITION_STATUS.posted,
  on_hold: REQUISITION_STATUS.onHold,
  filled: REQUISITION_STATUS.filled,
  closed_filled: REQUISITION_STATUS.filled,
  closed_cancelled: REQUISITION_STATUS.cancelled,
  archived: REQUISITION_STATUS.archived,
};

/** A requisition's state as his requisition status word (the loop facts of an application the reader is not seated on). */
export const requisitionWord = (state: string): string => REQUISITION_WORD[state] ?? state;

/** His five reasons a Recruiter designates a member for (BRD §9), in his order. */
export const REASONS_ADDED: readonly { value: string; label: string }[] = [
  { value: "finalist", label: P.finalists },
  { value: "alternate", label: P.alternates },
  { value: "silver_medalist", label: P.silverMedalists },
  { value: "timing_decline", label: P.timingDeclines },
  { value: "timing_withdrawal", label: P.withdrawals },
];
export const reasonWord = (slug: string | null | undefined): string => REASONS_ADDED.find((r) => r.value === slug)?.label ?? "—";

/** An application's stage as read: `held_from_stage` is the stage clock's (null when none was recorded, which the board
 *  reads as In HM Review); left out, the read does not carry it. */
export interface StageRead {
  stage?: string | null | undefined;
  held_from_stage?: string | null | undefined;
}

/** A held application whose read does not carry the stage it was held from: no column is guessed for it. */
const heldFromUnknown = (app: StageRead): boolean => app.stage === "held" && app.held_from_stage === undefined;

/** His DC-31 column for an application's stage, exactly as the Pipeline board places it (COLUMN_OF, through columnOf):
 *  Held stays in the column it was held from (Held itself when the read does not carry that stage); a Pass, a
 *  withdrawal or a close is the *Passed* strip. CA-4: the Step 8 word is never the stage. */
export function stageWord(app: StageRead): string | null {
  const stage = app.stage;
  if (!stage) return null;
  if (heldFromUnknown(app)) return HM_REVIEW_STATUS.held;
  const at = columnOf({ stage, held_from_stage: app.held_from_stage });
  if (at === "passed") return HM_REVIEW_STATUS.passed;
  return at ? G6_COLUMN_LABELS[at] : null;
}

const COLUMNS = Object.keys(G6_COLUMN_LABELS) as PipelineStage[];

/** Overview's stage track: the seven columns in board order — done before the application's column, on at it (gold
 *  while it is held there, "Held remains active with age and reason"), next after it. A closed application has none,
 *  and neither has a held one whose read does not carry the column it was held from. */
export function stageTrack(app: StageRead): RailStep[] | null {
  if (!app.stage || heldFromUnknown(app)) return null;
  const at = columnOf({ stage: app.stage, held_from_stage: app.held_from_stage });
  if (at === null || at === "passed") return null;
  const index = COLUMNS.indexOf(at);
  return COLUMNS.map((column, i) => ({
    label: G6_COLUMN_LABELS[column],
    state: i < index ? "done" : i > index ? "next" : app.stage === "held" ? "warn" : "on",
  }));
}

/** The stage clock the timeline read sent for an application (only where the reader sees its stage lines). */
export const clockOf = (clocks: readonly StageClock[] | undefined, applicationId: string | null | undefined): StageClock | undefined =>
  applicationId ? clocks?.find((clock) => clock.application_id === applicationId) : undefined;

/** The application the profile is read for now: the newest one the reader is seated on, live before closed. */
export function currentApplication(profile: Pick<CandidateProfileOutput, "applications">): ProfileApplication | undefined {
  const seated = [...(profile.applications ?? [])].reverse().filter((row) => row.application_id);
  return seated.find((row) => row.stage && !CLOSED_STAGES.has(row.stage)) ?? seated[0];
}

/** A résumé version's word: "Original resume" is the first version only; a later one reads as its tag (v1 · v2 · v3). */
export const resumeWord = (version: number): string => (version === 1 ? DOCUMENT_LABELS.originalResume : G7_TEMPLATES.version({ n: version }));

/** The parse behind each résumé version, from the timeline's upload lines (`candidate.resume_version` facts). */
export function parsesOf(rows: readonly TimelineRow[]): Map<number, string> {
  const out = new Map<number, string>();
  for (const row of rows) {
    const { version, parse_id: parse } = row.facts as { version?: unknown; parse_id?: unknown };
    if (row.kind === "candidate.resume_version" && typeof version === "number" && typeof parse === "string") out.set(version, parse);
  }
  return out;
}

const same = (a: string | null | undefined, b: string | null | undefined): boolean =>
  Boolean(a && b && a.trim().toLowerCase() === b.trim().toLowerCase());

/** The hero's line (CA-15): "<title> at <employer>" — an internal candidate's recorded title, else the employment row at
 *  the current employer (else the one still open, else the first); whichever of the two is known when one is missing. */
export function heroHeadline(profile: Pick<CandidateProfileOutput, "current_employer" | "employment_history" | "internal">): string | null {
  const history = profile.employment_history ?? [];
  const open = history.filter((row) => !row.end);
  const row = [...open, ...history].find((r) => same(r.employer, profile.current_employer)) ?? open[0] ?? history[0];
  const title = (profile.internal ?? []).find((record) => record.title)?.title ?? row?.title ?? null;
  const employer = profile.current_employer ?? row?.employer ?? null;
  if (title && employer) return PROFILE_TEMPLATES.titleAtEmployer({ title, employer });
  return title ?? employer ?? null;
}

// The merge and the recovery from a profile are the Admin's own acts (DC-228: the one Admin; no override path, the
// receipt carries the Admin's name and the reason in the call).

/** Owner changes are a TA Manager's or the Admin's (BRD 3.5 owner; his TA Admin folds into the Admin, DC-228; the
 *  server refuses anyone else). */
export const mayChangeOwner = (roles: readonly string[]): boolean =>
  roles.some((role) => role === "ta_manager" || role === "admin");

// ---- the timeline, duplicate review, edit, designate, merge and talent-community readings (no router import) -------
/** A line as History draws it (D-8, CA-3): the server's sentence in his words, its source channel as the source tag,
 *  and the ids as meta pills — the Candidate ID, and the requisition linking this person's application on it. */
export function timelineEvents(rows: readonly TimelineRow[]): HistoryEvent[] {
  return rows.map((row) => ({
    id: row.key,
    actor: row.actor_name ? { name: row.actor_name, hueKey: row.actor_id ?? row.actor_name } : undefined,
    sentence: row.sentence,
    at: row.at,
    source: row.channel,
    artifacts: [
      { label: row.candidate_no },
      ...(row.requisition_id
        ? [row.application_id
          ? { label: row.requisition_id, href: `#requisitions/${row.requisition_id}/candidates/${row.application_id}` }
          : { label: row.requisition_id }]
        : []),
    ],
  }));
}

const DUPLICATE_ROWS: readonly { key: string; label: string; reasons: readonly string[]; value: (side: DuplicateSide) => string | null | undefined }[] = [
  { key: "primary", label: G6_LABELS.primaryEmail, reasons: ["email"], value: (s) => s.email },
  { key: "secondary", label: G6_LABELS.secondaryEmail, reasons: ["secondary_email"], value: () => null },
  { key: "name", label: G6_LABELS.name, reasons: ["name"], value: (s) => s.display_name },
  { key: "phone", label: G6_LABELS.phone, reasons: ["phone"], value: (s) => s.phone },
  { key: "linkedin", label: G6_LABELS.linkedinUrl, reasons: ["linkedin"], value: (s) => s.linkedin_url },
  {
    key: "history", label: G6_LABELS.candidateHistory, reasons: ["history", "ats_id"],
    value: (s) => s.applications.map((fact) => `${fact.requisition_id} · ${fact.title}`).join(" | ") || null,
  },
];

/** The compare grid of a duplicate review: one column per profile; a matched attribute is gold on both. */
export function duplicateAttributes(review: Pick<CandidateDuplicateOpenOutput, "reasons" | "sides">): CompareAttribute[] {
  return DUPLICATE_ROWS.map((row) => {
    const matched = row.reasons.some((reason) => review.reasons.includes(reason));
    return {
      key: row.key,
      label: row.label,
      values: review.sides.map((side) => row.value(side) ?? null),
      matched: matched ? review.sides.map((_, index) => index) : [],
    };
  });
}

export type Editable = "display_name" | "primary_email" | "secondary_email" | "phone" | "linkedin_url" | "location" | "current_employer";
export const EDITABLE: readonly { key: Editable; label: string; hint?: string }[] = [
  { key: "display_name", label: L.name },
  { key: "primary_email", label: L.primaryEmail, hint: BRD.emailIdentity },
  { key: "secondary_email", label: L.secondaryEmail },
  { key: "phone", label: L.phone },
  { key: "linkedin_url", label: L.linkedinUrl },
  { key: "location", label: L.location },
  { key: "current_employer", label: BRD.currentEmployer },
];

/** The changed fields only (a field left as recorded is not sent); an emptied field clears. */
export function profileChanges(before: Record<Editable, string>, after: Record<Editable, string>, owner?: { before: string; after: string }): ProfileChanges {
  const changes: ProfileChanges = {};
  for (const { key } of EDITABLE) {
    const value = after[key].trim();
    if (value !== before[key].trim()) changes[key] = value || null;
  }
  if (owner && owner.after && owner.after !== owner.before) changes.owner_id = owner.after;
  return changes;
}

export const TAG = /^[a-z0-9][a-z0-9_-]{0,31}$/;
/** A tag as the server keeps it: lower case, spaces as hyphens. */
export const tagSlug = (text: string): string => text.trim().toLowerCase().replace(/\s+/g, "-");

export type KeepField = "display_name" | "primary_email" | "phone" | "linkedin_url";
export const MERGE_ROWS: readonly { key: KeepField; label: string; value: (p: CandidateProfileOutput) => string | null | undefined }[] = [
  { key: "display_name", label: G6_LABELS.name, value: (p) => p.display_name },
  { key: "primary_email", label: G6_LABELS.primaryEmail, value: (p) => p.email },
  { key: "phone", label: G6_LABELS.phone, value: (p) => p.phone },
  { key: "linkedin_url", label: G6_LABELS.linkedinUrl, value: (p) => p.linkedin_url },
];

/** The merge's `keep` from the preview's picks: a value taken from the profile not kept is the merged side's. */
export function mergeKeep(picks: Readonly<Record<string, number | undefined>>, kept: number): Record<KeepField, "survivor" | "merged"> {
  return Object.fromEntries(MERGE_ROWS.map(({ key }) => [key, picks[key] === undefined || picks[key] === kept ? "survivor" : "merged"])) as Record<KeepField, "survivor" | "merged">;
}

/** His five sets (BRD 3.5); the three reasons filter the membership, Internal talent the internal flag. */
export const SETS = [
  { key: "finalist", label: P.priorFinalists },
  { key: "alternate", label: P.alternates },
  { key: "silver_medalist", label: P.silverMedalists },
  { key: "internal", label: P.internalTalent },
  { key: "members", label: P.designatedMembers },
] as const;
export type SetKey = (typeof SETS)[number]["key"];
type Reason = NonNullable<CandidateSearchInput["reason_added"]>[number];

/** The search a set of chips asks: no chip is every designated member. */
export function searchOf(on: ReadonlySet<SetKey>, q: string): CandidateSearchInput {
  const reasons = (["finalist", "alternate", "silver_medalist"] as const).filter((key) => on.has(key)) as Reason[];
  const internalOnly = on.has("internal") && reasons.length === 0 && !on.has("members");
  return {
    q: q.trim() || null,
    community_only: !internalOnly,
    reason_added: reasons,
    internal: on.has("internal") ? true : null,
    sort: "date_added",
    limit: 100,
  };
}

